import React, { useState, useEffect, useCallback } from 'react';
import {
  Shield, AlertTriangle, Download, Filter, RefreshCw,
  CheckCircle, XCircle, TrendingUp, Activity, ChevronDown, ChevronUp, Eye, X,
} from 'lucide-react';
import apiClient from '../lib/apiClient';

// Every event type logAffiliateEvent() (src/lib/affiliateAuth.js) can write.
// Source of truth for the list, unioned with whatever the DB actually has —
// same pattern as SecurityAuditLogs.jsx's KNOWN_EVENT_TYPES.
const KNOWN_EVENT_TYPES = [
  'login_success', 'login_failure', 'account_locked',
  'password_set', 'password_reset_requested', 'password_reset_completed',
  'profile_updated',
];

const EVENT_BADGE = {
  login_success: 'bg-green-100 text-green-800',
  login_failure: 'bg-red-100 text-red-800',
  account_locked: 'bg-red-100 text-red-800',
  password_set: 'bg-blue-100 text-blue-800',
  password_reset_requested: 'bg-yellow-100 text-yellow-800',
  password_reset_completed: 'bg-blue-100 text-blue-800',
  profile_updated: 'bg-indigo-100 text-indigo-800',
};

const formatEventType = (t) => String(t || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export default function AffiliateAuditLog() {
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({ event_type: '', success: '', start_date: '', end_date: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [limit] = useState(50);
  const [offset, setOffset] = useState(0);
  const [seenEventTypes, setSeenEventTypes] = useState([]);
  const [selected, setSelected] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const fetchEvents = useCallback(async (isRefresh = false) => {
    isRefresh ? setRefreshing(true) : setLoading(true);
    setError(null);
    try {
      const params = { limit, offset };
      if (filters.event_type) params.event_type = filters.event_type;
      if (filters.success !== '') params.success = filters.success;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      const { data } = await apiClient.get('/affiliates/auth-events', { params });
      setEvents(data.events || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load affiliate sign-in activity');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filters, limit, offset]);

  const fetchStats = useCallback(async () => {
    try {
      const params = {};
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      const { data } = await apiClient.get('/affiliates/auth-events/stats', { params });
      setStats(data.stats);
      const found = Object.keys(data.stats?.byEventType || {}).filter(Boolean);
      setSeenEventTypes((prev) => {
        const merged = Array.from(new Set([...prev, ...found]));
        return merged.length === prev.length ? prev : merged;
      });
    } catch (err) {
      console.error('Failed to load affiliate auth event stats', err);
    }
  }, [filters.start_date, filters.end_date]);

  const updateFilters = (patch) => { setFilters((prev) => ({ ...prev, ...patch })); setOffset(0); };
  const clearFilters = () => { setFilters({ event_type: '', success: '', start_date: '', end_date: '' }); setOffset(0); };

  useEffect(() => { fetchEvents(); fetchStats(); }, [fetchEvents, fetchStats]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => { fetchEvents(true); fetchStats(); }, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchEvents, fetchStats]);

  const exportToCSV = () => {
    if (events.length === 0) return;
    const headers = ['Timestamp', 'Event Type', 'Affiliate', 'Email', 'Success', 'IP Address', 'User Agent'];
    const rows = events.map((e) => [
      new Date(e.created_at).toLocaleString(),
      e.event_type,
      e.affiliate?.name || 'Unknown (no account)',
      e.affiliate?.email || '',
      e.success ? 'Yes' : 'No',
      e.ip_address || '',
      e.user_agent || '',
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `affiliate_sign_in_activity_${new Date().toISOString().replace(/:/g, '-')}.csv`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    setTimeout(() => { document.body.removeChild(link); URL.revokeObjectURL(url); }, 100);
  };

  const eventTypeGroups = (() => {
    const counts = stats?.byEventType || {};
    const seen = Array.from(new Set([...seenEventTypes, ...(filters.event_type ? [filters.event_type] : [])]))
      .sort((a, b) => (counts[b]?.total || 0) - (counts[a]?.total || 0) || a.localeCompare(b));
    const unseen = KNOWN_EVENT_TYPES.filter((t) => !seen.includes(t)).sort((a, b) => a.localeCompare(b));
    return { seen, unseen, counts };
  })();

  const criticalEvents = events.filter((e) => !e.success);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center">
            <Shield className="w-8 h-8 text-violet-600 mr-3" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Affiliate Sign-In Activity</h1>
              <p className="text-sm text-gray-600 mt-1">Login, password, and account activity for the affiliate backoffice: separate from Security Audit Logs since affiliates aren't tenant users.</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`flex items-center px-4 py-2 rounded-lg border transition-colors ${autoRefresh ? 'bg-green-50 border-green-200 text-green-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
              Auto-refresh {autoRefresh ? 'ON' : 'OFF'}
            </button>
            <button onClick={() => setShowFilters(!showFilters)} className="flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
              <Filter className="w-4 h-4 mr-2" /> Filters {showFilters ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
            </button>
            <button onClick={exportToCSV} className="flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </button>
            <button onClick={() => fetchEvents(true)} disabled={refreshing} className="p-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50">
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      <div className="p-6">
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 flex items-center justify-between">
              <div><p className="text-sm font-medium text-gray-600">Total Events</p><p className="text-2xl font-bold text-gray-900 mt-1">{stats.total.toLocaleString()}</p></div>
              <Activity className="w-10 h-10 text-violet-500" />
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 flex items-center justify-between">
              <div><p className="text-sm font-medium text-gray-600">Successful</p><p className="text-2xl font-bold text-green-600 mt-1">{stats.success.toLocaleString()}</p></div>
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 flex items-center justify-between">
              <div><p className="text-sm font-medium text-gray-600">Failed</p><p className="text-2xl font-bold text-red-600 mt-1">{stats.failed.toLocaleString()}</p></div>
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 flex items-center justify-between">
              <div><p className="text-sm font-medium text-gray-600">Success Rate</p><p className="text-2xl font-bold text-blue-600 mt-1">{stats.total > 0 ? Math.round((stats.success / stats.total) * 100) : 0}%</p></div>
              <TrendingUp className="w-10 h-10 text-blue-500" />
            </div>
          </div>
        )}

        {criticalEvents.length > 0 ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <div className="flex items-center mb-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 mr-2" />
              <h3 className="text-sm font-semibold text-yellow-900">Failed events on this page ({criticalEvents.length})</h3>
            </div>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {criticalEvents.slice(0, 5).map((e, i) => (
                <div key={i} className="text-sm text-yellow-800 flex items-start">
                  <span className="inline-block w-2 h-2 bg-yellow-600 rounded-full mt-1.5 mr-2" />
                  <span>{formatEventType(e.event_type).toUpperCase()} — {e.affiliate?.email || 'Unknown (no account)'} — {new Date(e.created_at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-center">
            <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
            <p className="text-sm text-green-800">No failed affiliate sign-in events on this page</p>
          </div>
        )}

        {showFilters && (
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Event Type</label>
                <select value={filters.event_type} onChange={(e) => updateFilters({ event_type: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  <option value="">All Events</option>
                  {eventTypeGroups.seen.length > 0 && (
                    <optgroup label="In your logs">
                      {eventTypeGroups.seen.map((t) => <option key={t} value={t}>{formatEventType(t)}{eventTypeGroups.counts[t]?.total ? ` (${eventTypeGroups.counts[t].total})` : ''}</option>)}
                    </optgroup>
                  )}
                  {eventTypeGroups.unseen.length > 0 && (
                    <optgroup label="Tracked, none recorded yet">
                      {eventTypeGroups.unseen.map((t) => <option key={t} value={t}>{formatEventType(t)}</option>)}
                    </optgroup>
                  )}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select value={filters.success} onChange={(e) => updateFilters({ success: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg">
                  <option value="">All</option><option value="true">Success</option><option value="false">Failed</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
                <div className="flex space-x-2">
                  <input type="date" value={filters.start_date} onChange={(e) => updateFilters({ start_date: e.target.value })} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg" />
                  <input type="date" value={filters.end_date} onChange={(e) => updateFilters({ end_date: e.target.value })} className="flex-1 px-3 py-2 border border-gray-300 rounded-lg" />
                </div>
              </div>
            </div>
            <div className="mt-4"><button onClick={clearFilters} className="text-sm text-gray-600 hover:text-gray-900">Clear Filters</button></div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-center">
            <XCircle className="w-5 h-5 text-red-600 mr-2" /><p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200"><h2 className="text-lg font-semibold text-gray-900">Sign-In Events</h2></div>
          {loading ? (
            <div className="flex justify-center items-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" /></div>
          ) : events.length === 0 ? (
            <div className="text-center py-12"><Shield className="w-12 h-12 text-gray-300 mx-auto mb-3" /><p className="text-gray-500">No affiliate sign-in activity found</p></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Event Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Affiliate</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP Address</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {events.map((e) => (
                    <tr key={e.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{new Date(e.created_at).toLocaleString()}</td>
                      <td className="px-6 py-4 whitespace-nowrap"><span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${EVENT_BADGE[e.event_type] || 'bg-gray-100 text-gray-600'}`}>{formatEventType(e.event_type)}</span></td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {e.affiliate ? <>{e.affiliate.name} <span className="text-gray-400">· {e.affiliate.email}</span></> : <span className="text-gray-400 italic">Unknown (no account)</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {e.success ? <span className="flex items-center text-sm text-green-600"><CheckCircle className="w-4 h-4 mr-1" />Success</span> : <span className="flex items-center text-sm text-red-600"><XCircle className="w-4 h-4 mr-1" />Failed</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{e.ip_address || 'N/A'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button onClick={() => setSelected(e)} className="text-indigo-600 hover:text-indigo-900 flex items-center"><Eye className="w-4 h-4 mr-1" />View</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!loading && events.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">Showing {offset + 1} to {offset + events.length}</div>
              <div className="flex space-x-2">
                <button onClick={() => setOffset(Math.max(0, offset - limit))} disabled={offset === 0} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">Previous</button>
                <button onClick={() => setOffset(offset + limit)} disabled={events.length < limit} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">Next</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Event Details</h2>
              <button onClick={() => setSelected(null)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-sm font-medium text-gray-600">Event Type</p><p className="mt-1"><span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${EVENT_BADGE[selected.event_type] || 'bg-gray-100 text-gray-600'}`}>{selected.event_type}</span></p></div>
                <div><p className="text-sm font-medium text-gray-600">Status</p><p className="mt-1 flex items-center">{selected.success ? <><CheckCircle className="w-4 h-4 text-green-600 mr-1" />Success</> : <><XCircle className="w-4 h-4 text-red-600 mr-1" />Failed</>}</p></div>
                <div><p className="text-sm font-medium text-gray-600">Timestamp</p><p className="mt-1 text-sm text-gray-900">{new Date(selected.created_at).toLocaleString()}</p></div>
                <div><p className="text-sm font-medium text-gray-600">Affiliate</p><p className="mt-1 text-sm text-gray-900">{selected.affiliate ? `${selected.affiliate.name} (${selected.affiliate.email})` : 'Unknown (no account)'}</p></div>
                <div><p className="text-sm font-medium text-gray-600">IP Address</p><p className="mt-1 text-sm text-gray-900">{selected.ip_address || 'N/A'}</p></div>
              </div>
              {selected.user_agent && <div><p className="text-sm font-medium text-gray-600">User Agent</p><p className="mt-1 text-sm text-gray-900 break-all">{selected.user_agent}</p></div>}
              {selected.metadata && Object.keys(selected.metadata).length > 0 && (
                <div><p className="text-sm font-medium text-gray-600 mb-2">Metadata</p><pre className="bg-gray-50 p-3 rounded-lg text-xs overflow-x-auto">{JSON.stringify(selected.metadata, null, 2)}</pre></div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
